import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { LoginService } from '../../core/api/login.api';
import { PowerBIReportService } from '../../core/api/power-bi-report.api';
import { SessionService } from '../../core/auth/session.service';
import { AppVersionService } from '../../core/version/app-version.service';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent implements OnInit {
  emailAddress = '';
  password = '';
  loginInfo: any;
  validLogin = true;
  verifiedEmail = true;
  errorMessage = '';
  access_token = '';
  embed_report_url = '';
  embed_report_id = '';
  result: any;

  constructor(
    private loginService: LoginService,
    private powerBIReportService: PowerBIReportService,
    private session: SessionService,
    private router: Router,
    public appVersion: AppVersionService
  ) {
    const email = this.session.emailAddress();
    const password = this.session.password();
    if (email) {
      this.emailAddress = email;
    }
    if (password) {
      this.password = password;
    }
  }

  ngOnInit(): void {
    this.checkForKey();
  }

  async getLoginInfo(): Promise<void> {
    sessionStorage.clear();
    document.body.style.cursor = 'wait';
    this.validLogin = true;
    this.verifiedEmail = true;

    try {
      const result = await firstValueFrom(
        this.loginService.authenticate({
          email: this.emailAddress,
          password: this.password,
        })
      );

      this.loginInfo = result;

      if (!this.loginInfo || this.loginInfo.portalID == null) {
        this.validLogin = false;
        document.body.style.cursor = 'auto';
        return;
      }
      switch (this.loginInfo.portalID) {
        case 1: {
          const dateVerified = this.loginInfo.dateVerified;
          const days = this.loginInfo.days;
          const emailAddress = this.loginInfo.emailAddress;
          const emailLoginID = this.loginInfo.emailLoginID;
          const emailAddressVerificationID = this.loginInfo.emailAddressVerificationID;
          const name = this.loginInfo.name;

          const currentDate = new Date();
          const dateVerifiedCompare = new Date(dateVerified);
          const futureDate = this.addDaysToDate(dateVerifiedCompare, days);

          if (!dateVerified || dateVerified === '' || currentDate > futureDate) {
            this.verifiedEmail = false;
            try {
              this.result = await firstValueFrom(
                this.loginService.verifyEmail(
                  emailAddress,
                  emailLoginID,
                  emailAddressVerificationID,
                  name
                )
              );
            } catch (error: any) {
              this.errorMessage = error?.error?.message ?? error?.message;
              alert('There was an issue generating the email token, please try logging in again.');
            }
            document.body.style.cursor = 'auto';
            return;
          }

          this.setPortal1Cookies();
          await this.router.navigateByUrl(`/dashboard/${this.loginInfo.emailLoginID}`);
          if (this.loginInfo.premiumReports) {
            await this.checkReportPermissions();
          }
          break;
        }
        case 2:
          this.session.set('emailloginid', this.loginInfo.emailLoginID);
          this.session.set('portalid', this.loginInfo.portalID);
          this.session.set('loginname', this.loginInfo.name);
          this.session.set('inspectorid', this.loginInfo.inspectorid);
          this.session.set('inspectionmanager', this.loginInfo.inspectionManager);
          this.session.set(
            'allowInspectionUploadFromPortal',
            this.loginInfo.allowInspectionUploadFromPortal
          );
          this.session.set('uploadFromPortalOverride', this.loginInfo.uploadFromPortalOverride);
          this.session.set('isIPPayInspector', this.loginInfo.isIPPayInspector);
          this.session.set('payScaleDescription', this.loginInfo.payScaleDescription);
          this.session.set('enablelotobservation', this.loginInfo.enableLotObservation);
          await this.router.navigateByUrl(`/inspectordashboard/${this.loginInfo.emailLoginID}`);
          break;
        case 3:
          this.session.set('emailloginid', this.loginInfo.emailLoginID);
          this.session.set('emailaddress', this.loginInfo.emailAddress);
          this.session.set('loginname', this.loginInfo.name);
          this.session.set('password', this.loginInfo.password);
          this.session.set('portalid', this.loginInfo.portalID);
          this.session.set('ptadmin', this.loginInfo.ptAdmin);
          this.session.set('cableid', this.loginInfo.cableID);
          this.session.set('inspectionmanager', this.loginInfo.inspectionManager);
          await this.router.navigateByUrl('/ptportal');
          break;
        case 4:
          this.session.set('emailloginid', this.loginInfo.emailLoginID);
          this.session.set('emailaddress', this.loginInfo.emailAddress);
          this.session.set('loginname', this.loginInfo.name);
          this.session.set('portalid', this.loginInfo.portalID);
          await this.router.navigateByUrl('/engineeringPlanVault');
          break;
        case 5:
          this.session.set('emailloginid', this.loginInfo.emailLoginID);
          this.session.set('emailaddress', this.loginInfo.emailAddress);
          this.session.set('loginname', this.loginInfo.name);
          this.session.set('portalid', this.loginInfo.portalID);
          this.session.set('hvaccontractorid', this.loginInfo.hvacContractorID);
          await this.router.navigateByUrl('/hvacDashboard');
          break;
        default:
          await this.router.navigateByUrl('/');
          break;
      }
    } catch {
      this.validLogin = false;
    } finally {
      document.body.style.cursor = 'auto';
    }
  }

  private setPortal1Cookies(): void {
    this.session.set('emailloginid', this.loginInfo.emailLoginID);
    this.session.set('emailaddress', this.loginInfo.emailAddress);
    this.session.set('password', this.loginInfo.password);
    this.session.set('skey', this.loginInfo.skey);
    this.session.set('amkey', this.loginInfo.amkey);
    this.session.set('inspectionmanager', this.loginInfo.inspectionManager);
    this.session.set('builderid', this.loginInfo.builderid);
    this.session.set('supername', this.loginInfo.name);
    this.session.set('loginname', this.loginInfo.name);
    this.session.set('builderaccess', this.loginInfo.builderAccess);
    this.session.set('webappaccesslevel', this.loginInfo.webAppAccessLevel);
    this.session.set('portalid', this.loginInfo.portalID);
    this.session.set('closeoutprocess', this.loginInfo.closeOutProcess);
    this.session.set('premiumreports', this.loginInfo.premiumReports);
    this.session.set('premiumreportquartz', this.loginInfo.premiumReportQuartz);
    this.session.set('premiumreportdefects', this.loginInfo.premiumReportDefects);
  }

  async checkReportPermissions(): Promise<void> {
    if (this.loginInfo.premiumReportQuartz) {
      try {
        const result = await firstValueFrom(
          this.powerBIReportService.getPBIQuartzReport(this.loginInfo.emailAddress)
        );
        this.access_token = result.embedToken.token;
        this.embed_report_url = result.embedReports[0].embedUrl;
        this.embed_report_id = result.embedReports[0].reportId;
        this.session.set('access_token_quartz', this.access_token);
        this.session.set('embed_report_url_quartz', this.embed_report_url);
        this.session.set('embed_report_id_quartz', this.embed_report_id);
      } catch (error: any) {
        this.errorMessage = error?.error?.message ?? error?.message;
      }
    }

    if (this.loginInfo.premiumReportDefects) {
      try {
        const result = await firstValueFrom(
          this.powerBIReportService.getPBIDefectsReport(this.loginInfo.emailAddress)
        );
        this.access_token = result.embedToken.token;
        this.embed_report_url = result.embedReports[0].embedUrl;
        this.embed_report_id = result.embedReports[0].reportId;
        this.session.set('access_token_defects', this.access_token);
        this.session.set('embed_report_url_defects', this.embed_report_url);
        this.session.set('embed_report_id_defects', this.embed_report_id);
      } catch (error: any) {
        this.errorMessage = error?.error?.message ?? error?.message;
      }
    }
  }

  resetPassword(): void {
    this.router.navigateByUrl('/resetpassword');
  }

  addDaysToDate(date: Date, days: number): Date {
    const newDate = new Date(date);
    newDate.setDate(newDate.getDate() + days);
    return newDate;
  }

  checkForKey(): void {
    if (!this.session.emailLoginId()) {
      return;
    }
    const portal = parseInt(this.session.portalId() || '0', 10);
    if (portal === 1) {
      this.router.navigateByUrl(`/dashboard/${this.session.emailLoginId()}`);
    } else if (portal === 2) {
      this.router.navigateByUrl(`/inspectordashboard/${this.session.emailLoginId()}`);
    } else if (portal === 5) {
      this.router.navigateByUrl('/hvacDashboard');
    }
  }
}
